/**
 * PastOrdersModal - Modal displaying list of past orders
 */

import React, { useRef, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import { CompletedOrder, OrderPayload } from './types';
import { PastOrderItem } from './PastOrderItem';
import { OrderPrintTemplate } from './OrderPrintTemplate';
import { UIText } from './translations';

interface PastOrdersModalProps {
	isOpen: boolean;
	onClose: () => void;
	completedOrders: CompletedOrder[];
	onDeleteOrder: (key: string) => void;
	translations: UIText['pastOrders'];
	printTranslations: UIText['print'];
	eventName: string;
}

export const PastOrdersModal: React.FC<PastOrdersModalProps> = ({
	isOpen,
	onClose,
	completedOrders,
	onDeleteOrder,
	translations,
	printTranslations,
	eventName,
}) => {
	// Print functionality state
	const contentRef = useRef<HTMLDivElement>(null);
	const [orderToPrint, setOrderToPrint] = React.useState<OrderPayload | null>(null);

	const printFn = useReactToPrint({
		contentRef,
		documentTitle: `SprintRay-PastOrder`,
		preserveAfterPrint: true,
		print: (printIframe) =>
			new Promise<void>((resolve) => {
				printIframe.style.display = 'none';
				printIframe.contentWindow?.print();
				resolve();
			}),
		onPrintError: (error: any) => {
			console.error('Print error:', error);
		},
	});

	// Handle print request
	const handlePrint = (order: OrderPayload) => {
		setOrderToPrint(order);
		// Small delay to ensure the order is set before printing
		setTimeout(() => {
			printFn();
		}, 100);
	};

	// Handle backdrop click to close
	const handleBackdropClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	const itemTranslations = {
		customer: translations.customer,
		date: translations.date,
		total: translations.total,
		print: translations.print,
		delete: translations.delete,
		items: translations.items,
		promotions: translations.promotions,
		synced: translations.synced,
		pending: translations.pending,
	};

	return (
		<>
			<div
				className="sprintry-past-orders-modal"
				onClick={handleBackdropClick}
			>
				<div className="sprintry-past-orders-modal__content">
					<div className="sprintry-past-orders-modal__header">
						<h3 className="sprintry-past-orders-modal__title">{translations.title}</h3>
						<button
							type="button"
							className="sprintry-past-orders-modal__close"
							onClick={onClose}
							aria-label={translations.close}
						>
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<line x1="18" y1="6" x2="6" y2="18" />
								<line x1="6" y1="6" x2="18" y2="18" />
							</svg>
						</button>
					</div>

					<div className="sprintry-past-orders-modal__body">
						{completedOrders.length === 0 ? (
							<div className="sprintry-past-orders-modal__empty">
								<svg
									width="48"
									height="48"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<circle cx="12" cy="12" r="10" />
									<polyline points="12 6 12 12 16 14" />
								</svg>
								<p className="sprintry-past-orders-modal__empty-text">
									{translations.empty}
								</p>
								<p className="sprintry-past-orders-modal__empty-hint">
									{translations.emptyHint}
								</p>
							</div>
						) : (
							<div className="sprintry-past-orders-modal__list">
								{completedOrders.map((completedOrder) => (
									<PastOrderItem
										key={completedOrder.savedAt}
										completedOrder={completedOrder}
										onPrint={handlePrint}
										onDelete={onDeleteOrder}
										translations={itemTranslations}
									/>
								))}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Hidden Print Template */}
			{orderToPrint && (
				<div style={{ display: 'none' }}>
					<OrderPrintTemplate
						ref={contentRef}
						order={orderToPrint}
						eventName={eventName}
						translations={printTranslations}
						title={printTranslations.title}
					/>
				</div>
			)}
		</>
	);
};
